import styled from 'styled-components';
import { alpha } from '../../../themes/brand';

export const Container = styled.div`
  padding: 24px;
  background-color: ${({theme}) => theme.background};
  height: 100%;
  overflow-x: hidden;
  max-width: 100%;
  box-sizing: border-box;

  /* Responsividade para tablets */
  @media (max-width: 1024px) {
    padding: 20px;
  }

  /* Responsividade para mobile */
  @media (max-width: 768px) {
    padding: 16px;
  }

  /* Mobile pequeno */
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

export const Header = styled.div`
  position: fixed;
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  z-index: 10;
`;

export const BackButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  .icon {
    color: ${({ theme }) => theme.arrowBack};
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: translateX(-2px);
  }
`;

export const TitleContainer = styled.div`
  margin-top: 50px;
  max-width: 100%;
  width: 100%;
`;

export const Title = styled.h1`
  color: ${props => props.theme.text};
  margin: 0;
  font-size: 32px;
  font-weight: 700;

  /* Responsividade para tablets */
  @media (max-width: 1024px) {
    font-size: 28px;
  }

  /* Responsividade para mobile */
  @media (max-width: 768px) {
    font-size: 24px;
  }

  /* Mobile pequeno */
  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

export const Subtitle = styled.p`
  color: ${props => props.theme.textSubtitleCompliance};
  margin: 8px 0 32px 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
`;

export const FormTitle = styled.h2`
  color: ${props => props.theme.text};
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
`;

export const FormDescription = styled.p`
  color: ${props => props.theme.text};
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
`;

export const RequiredFieldsNote = styled.p`
  color: ${props => props.theme.text};
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 400;
  opacity: 0.8;
  
  &::before {
    content: "* ";
    color: ${({ theme }) => theme.primaryAccent};
    margin-right: 4px;
  }
`;

export const FormContainer = styled.div`
  width: 100%;
  padding: 0 40px;
  box-sizing: border-box;
  max-width: 1200px;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 100%;
`;

export const FormGroup = styled.div`
  margin-bottom: 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const Label = styled.label`
  flex: 0 0 35%;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  color: ${props => props.theme.text};
  
  @media (max-width: 768px) {
    margin-bottom: 8px;
    width: 100%;
  }
`;

export const Input = styled.input`
  flex: 1 1 auto;
  min-width: 300px;
  height: 48px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid ${({theme}) => theme.borderInputCompliance};
  background-color: ${({theme}) => theme.formUserInput || theme.placeholderColor};
  color: ${({theme}) => theme.text};
  font-size: 16px;
  box-sizing: border-box;
  margin-left: auto;
  
  &:focus {
    outline: none;
    border: 1px solid ${({ theme }) => theme.primaryAccent};
  }
  
  &:disabled {
    background-color: ${({theme}) => theme.disabledBackground || theme.background};
    cursor: not-allowed;
  }

  /* Responsividade para mobile */
  @media (max-width: 768px) {
    min-width: 100%;
    margin-left: 0;
    margin-top: 8px;
  }

  /* Mobile pequeno */
  @media (max-width: 480px) {
    height: 44px;
    padding: 0 12px;
    font-size: 16px; /* Evita zoom no iOS */
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  padding-right: 5px;
`;

export const SubmitButton = styled.button`
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primaryOn};
  border: none;
  border-radius: 30px;
  padding: 10px 28px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.primary};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.primary};
    cursor: not-allowed;
  }
`;

export const TimelineContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  width: 100%;
  position: relative;
`;

export const TimelineProgress = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  height: 4px;
  background-color: ${({ theme }) => theme.primary};
  transform: translateY(-50%);
  z-index: 1;
  transition: width 0.3s ease;
`;

export const TimelineBackground = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  height: 4px;
  width: 100%;
  background-color: ${({theme}) => theme.borderInputCompliance};
  transform: translateY(-50%);
`;

export const StepItem = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
`;

export const StepCircle = styled.div<{ active: boolean; completed: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  margin-bottom: 8px;
  background-color: ${({ active, completed, theme }) => 
    completed ? theme.primary : active ? theme.card : theme.formUserInput};
  color: ${({ active, completed, theme }) => 
    completed ? theme.primaryOn : active ? theme.primaryAccent : theme.text};
  border: 2px solid ${({ theme, active, completed }) => 
    completed || active ? theme.primaryAccent : 'transparent'};
  transition: all 0.3s ease;
`;

export const StepLabel = styled.span<{ active: boolean }>`
  font-size: 12px;
  color: ${({ active, theme }) => active ? theme.primaryAccent : theme.text};
  font-weight: ${({ active }) => active ? '600' : '400'};
`;

export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

export const SectionTitle = styled.h3`
  color: ${props => props.theme.text};
  margin: 0;
  font-size: 20px;
  font-weight: 600;
`;

export const SectionDescription = styled.p`
  color: ${props => props.theme.textSubtitleCompliance};
  margin: 8px 0 0 0;
  font-size: 14px;
  font-weight: 400;
`;

export const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
`;

export const PrevButton = styled.button`
  background-color: transparent;
  color: ${({theme}) => theme.text};
  border: 1px solid ${({theme}) => theme.borderInputCompliance};
  border-radius: 30px;
  padding: 10px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: ${({ theme }) => alpha(theme.primary, 0.1)};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const NextButton = styled.button`
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primaryOn};
  border: none;
  border-radius: 30px;
  padding: 10px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: ${({ theme }) => theme.primary};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.primary};
    cursor: not-allowed;
  }
`;

export const SectionStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 16px;
`;

export const StatusText = styled.span`
  font-size: 14px;
  color: ${({theme}) => theme.textSubtitleCompliance};
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${({theme}) => theme.borderInputCompliance};
  border-radius: 4px;
  margin-top: 8px;
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background-color: ${({ theme }) => theme.primary};
  width: ${({ progress }) => `${progress}%`};
  transition: width 0.3s ease;
  border-radius: 4px;
`;

export const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin-right: 8px;
  cursor: pointer;
`;

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  color: ${props => props.theme.text};
  font-size: 16px;
  font-weight: 500;
`;

export const CheckboxContainer = styled.div`
  display: flex;
  flex: 0 0 60%;
  align-items: center;
`;

export const ConditionalField = styled.div<{ visible: boolean }>`
  margin-top: ${({ visible }) => visible ? '16px' : '0'};
  margin-bottom: ${({ visible }) => visible ? '16px' : '0'};
  height: ${({ visible }) => visible ? 'auto' : '0'};
  opacity: ${({ visible }) => visible ? '1' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  width: 100%;
  
  ${FormGroup} {
    margin-left: 0;
  }
  
  ${Input} {
    min-width: 300px;
    margin-left: auto;
  }
`;

export const CheckboxGroup = styled.div`
  display: flex;
  flex: 0 0 60%;
  gap: 24px;
`;

export const CheckboxOption = styled.div`
  display: flex;
  align-items: center;
`;

export const RadioGroup = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  min-width: 300px;
  margin-left: auto;
`;

export const RadioOption = styled.div`
  display: flex;
  align-items: center;
`;

export const Radio = styled.input`
  width: 18px;
  height: 18px;
  margin-right: 8px;
  cursor: pointer;
`;

export const RadioLabel = styled.label`
  color: ${props => props.theme.text};
  font-size: 16px;
  cursor: pointer;
`;

export const CheckboxGroupHorizontal = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  flex: 1 1 auto;
  min-width: 300px;
  margin-left: auto;
`;

export const CheckboxWithLabel = styled.div`
  display: flex;
  align-items: center;
  margin-right: 24px;
`; 
/**
 * Asterisco de campo obrigatório.
 *
 * Era `<span style={{ color: 'red' }}>*</span>` repetido **34 vezes** no `FormPage.tsx` — o mesmo
 * estilo inline copiado a cada campo. Além de cor literal, era 34 lugares para errar: mudar o tom
 * exigia 34 edições, e uma esquecida passa despercebida.
 *
 * `error` e não um vermelho qualquer: o asterisco comunica exigência, que é a mesma semântica da
 * validação. Assim ele acompanha o tema e o contraste já validado.
 */
export const Obrigatorio = styled.span`
  color: ${({ theme }) => theme.error};
  margin-left: 2px;
`;
